// Flyout dos grupos com submenu no rail da sidebar (Fase 17.8, Plano 10; U-09, D-17).
// Abre por hover (atraso de 120ms, só com mouse), por clique ou toque e por Enter, Espaço ou seta direita; o foco vai ao primeiro
// subitem, as setas navegam e Esc fecha e devolve o foco ao item. Nada depende só de hover: o tablet abre por toque.
import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactElement, type ReactNode } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { SidebarMenuButton, SidebarMenuItem, useSidebar } from '../ui/sidebar';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { chaveGrupo, chaveItem, rotuloAcessivel, rotuloComPendencia } from '../../lib/painel/pendencias';
import { resolverDestinoNavegacao, type GrupoNavegacao } from './navegacao';
import { SidebarBadge } from './sidebar-badge';

export interface FlyoutSubmenuProps {
  readonly grupo: GrupoNavegacao;
  readonly pathname: string;
  readonly aberto: boolean;
  readonly onAbertoChange: (aberto: boolean) => void;
  /** Ícone do grupo (o mesmo do item expandido). */
  readonly icone: ReactNode;
  /** Contagem de pendências por id de subitem (Plano 13: badge de número em cada subitem). */
  readonly contagens?: Readonly<Record<string, number>>;
  /** Soma do grupo: vira o ponto do item no rail e a contagem do tooltip e do nome acessível (Plano 13). */
  readonly contagemGrupo?: number;
  /** Chaves (`grupo:{id}`, `item:{id}`) cujo badge deve pulsar (decididas por `devePulsar` no hook das pendências). */
  readonly pulsos?: ReadonlySet<string>;
}

const ATRASO_ABRIR_HOVER_MS = 120;
const ATRASO_FECHAR_HOVER_MS = 160;
// O botão do item ocupa a coluna do rail menos 8px de padding de cada lado e a borda de 1px (64 - 16 - 1 = 47px de largura): a
// âncora termina 9px antes da borda do rail. O flyout abre a 8px da borda, então o afastamento da âncora soma os dois.
const FOLGA_ANCORA_ATE_BORDA_PX = 9;
const RESPIRO_DA_BORDA_PX = 8;
const AFASTAMENTO_DO_FLYOUT_PX = FOLGA_ANCORA_ATE_BORDA_PX + RESPIRO_DA_BORDA_PX;

export function FlyoutSubmenu({ grupo, pathname, aberto, onAbertoChange, icone, contagens, contagemGrupo = 0, pulsos }: FlyoutSubmenuProps): ReactElement {
  const { isMobile, setOpenMobile } = useSidebar();
  const destinoAtivo = resolverDestinoNavegacao(pathname, [grupo]);
  const grupoAtivo = destinoAtivo !== undefined;
  const temporizador = useRef<number | undefined>(undefined);
  const abertoPorHover = useRef(false);
  const conteudo = useRef<HTMLDivElement>(null);
  const [dicaAberta, definirDicaAberta] = useState(false);

  useEffect(() => () => window.clearTimeout(temporizador.current), []);
  // Navegar fecha o flyout (o clique no subitem já o fecha; isto cobre navegação por outros caminhos).
  const caminhoAnterior = useRef(pathname);
  useEffect(() => {
    if (caminhoAnterior.current === pathname) return;
    caminhoAnterior.current = pathname;
    onAbertoChange(false);
  }, [pathname, onAbertoChange]);

  const limpar = (): void => window.clearTimeout(temporizador.current);
  const agendar = (valor: boolean, atraso: number): void => {
    limpar();
    temporizador.current = window.setTimeout(() => {
      if (valor) abertoPorHover.current = true;
      onAbertoChange(valor);
    }, atraso);
  };
  const aoMudarAberto = (valor: boolean): void => {
    if (!valor) abertoPorHover.current = false;
    onAbertoChange(valor);
  };
  const soMouse = (evento: PointerEvent<HTMLElement>): boolean => evento.pointerType === 'mouse';

  const aoClicarNoGatilho = (evento: { preventDefault: () => void }): void => {
    // O clique decide sozinho: quem abriu por hover só consolida a abertura, e um segundo clique fecha.
    evento.preventDefault();
    limpar();
    if (aberto && !abertoPorHover.current) {
      onAbertoChange(false);
      return;
    }
    abertoPorHover.current = false;
    onAbertoChange(true);
  };

  const aoTeclarNoGatilho = (evento: KeyboardEvent<HTMLElement>): void => {
    if (evento.key === 'ArrowRight' && !aberto) {
      evento.preventDefault();
      abertoPorHover.current = false;
      onAbertoChange(true);
    }
  };

  const aoTeclarNoConteudo = (evento: KeyboardEvent<HTMLElement>): void => {
    const links = Array.from(evento.currentTarget.querySelectorAll<HTMLElement>('a[href]'));
    const atual = links.indexOf(document.activeElement as HTMLElement);
    let destino: number | undefined;
    if (evento.key === 'ArrowDown') destino = atual < 0 ? 0 : (atual + 1) % links.length;
    else if (evento.key === 'ArrowUp') destino = atual <= 0 ? links.length - 1 : atual - 1;
    else if (evento.key === 'Home') destino = 0;
    else if (evento.key === 'End') destino = links.length - 1;
    else if (evento.key === 'ArrowLeft') {
      evento.preventDefault();
      onAbertoChange(false);
      return;
    }
    if (destino === undefined) return;
    evento.preventDefault();
    links[destino]?.focus();
  };

  const fecharAoNavegar = (): void => {
    onAbertoChange(false);
    if (isMobile) setOpenMobile(false);
  };

  return (
    <SidebarMenuItem>
      <Popover open={aberto} onOpenChange={aoMudarAberto}>
        <Tooltip open={dicaAberta && !aberto} onOpenChange={definirDicaAberta}>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild onClick={aoClicarNoGatilho}>
              <SidebarMenuButton
                type="button"
                isActive={grupoAtivo}
                aria-label={contagemGrupo > 0 ? `${grupo.rotulo}: abrir destinos, ${rotuloAcessivel(contagemGrupo)}` : `${grupo.rotulo}: abrir destinos`}
                data-testid={`sidebar-group-${grupo.id}`}
                onKeyDown={aoTeclarNoGatilho}
                onPointerEnter={(evento) => { if (soMouse(evento)) agendar(true, ATRASO_ABRIR_HOVER_MS); }}
                onPointerLeave={(evento) => {
                  if (!soMouse(evento)) return;
                  if (aberto && abertoPorHover.current) agendar(false, ATRASO_FECHAR_HOVER_MS);
                  else limpar();
                }}
              >
                {icone}<span className="sidebar-rotulo">{grupo.rotulo}</span>
                <SidebarBadge modulo={grupo.id} contagem={contagemGrupo} variante="ponto" pulsar={pulsos?.has(chaveGrupo(grupo.id)) ?? false} />
              </SidebarMenuButton>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={8} data-tooltip-menu>{rotuloComPendencia(grupo.rotulo, contagemGrupo)}</TooltipContent>
        </Tooltip>
        <PopoverContent
          ref={conteudo}
          side="right"
          align="start"
          sideOffset={AFASTAMENTO_DO_FLYOUT_PX}
          data-theme="mineral-nav"
          data-testid={`sidebar-menu-${grupo.id}`}
          aria-label={grupo.rotulo}
          className="flyout-submenu"
          onKeyDown={aoTeclarNoConteudo}
          onPointerEnter={(evento) => { if (soMouse(evento)) limpar(); }}
          onPointerLeave={(evento) => { if (soMouse(evento) && abertoPorHover.current) agendar(false, ATRASO_FECHAR_HOVER_MS); }}
          // O foco automático do Radix pula links; o primeiro subitem recebe o foco à mão. Aberto por hover, o foco não é roubado.
          onOpenAutoFocus={(evento) => {
            evento.preventDefault();
            if (!abertoPorHover.current) conteudo.current?.querySelector<HTMLElement>('a[href]')?.focus();
          }}
        >
          <p className="flyout-submenu__titulo">{grupo.rotulo}</p>
          {grupo.itens?.map((item) => {
            const ativo = destinoAtivo?.item?.id === item.id;
            const contagem = contagens?.[item.id] ?? 0;
            return (
              <a
                key={item.id}
                href={item.caminho}
                onClick={fecharAoNavegar}
                aria-current={ativo ? 'page' : undefined}
                title={item.rotulo}
                className="flyout-submenu__item"
                data-testid={`sidebar-subitem-${item.id}`}
              >
                <span className="flyout-submenu__rotulo">{item.rotulo}</span>
                <SidebarBadge modulo={item.id} contagem={contagem} pulsar={pulsos?.has(chaveItem(item.id)) ?? false} />
                {ativo && <span className="sr-only">, página atual</span>}
              </a>
            );
          })}
        </PopoverContent>
      </Popover>
    </SidebarMenuItem>
  );
}
